// Skripte und Drehbücher: Liste, Editor mit Laufzeit, Versionen, PDF und Teleprompter.
// Eingaben ändern nur das Modell und die betroffenen Anzeigen; neu gezeichnet wird nur bei Strukturänderungen.
import type { Client, Project } from "../shared/types.js";
import {
  BRIEF_KEYS,
  FIELD_KEYS,
  FORMATS,
  PRODUCTION_KEYS,
  SCRIPT_STATUS,
  SCRIPT_TYPES,
  SHOT_SIZES,
  beatIsEstimated,
  beatSeconds,
  beatsOf,
  fieldValue,
  fmtSeconds,
  fmtTc,
  newBeat,
  newQuestion,
  nid,
  parseLength,
  parseSound,
  placesOf,
  planByLocation,
  shotCount,
  syncBeat,
  targetSeconds,
  timecodes,
  totalSeconds,
  videoLabel,
  videoSeconds,
  videoTemplate,
  type ScriptBeat,
  type ScriptContent,
  type ScriptField,
  type ScriptFull,
  type ScriptListItem,
  type ScriptStatus,
  type ScriptType,
  type ScriptVersionRow,
  type ScriptVideo,
} from "../shared/script.js";
import { ApiError, api } from "./api.js";
import { ic } from "./icons.js";
import { esc, fmtDate } from "./util.js";

export interface ScriptHost {
  clients: () => Client[];
  projects: () => Project[];
  go: (to: string) => void;
  rerender: () => void;
  toast: (msg: string) => void;
  openModal: (html: string, cls?: string) => void;
  closeModal: () => void;
  confirm: (text: string, label: string, fn: () => void | Promise<void>) => void;
  mhead: (t: string) => string;
  pageHead: (eyebrow: string, title: string, lede: string, acts?: string) => string;
  emptyBox: (title: string, text: string, btn?: string) => string;
  authLost: (e: unknown) => boolean;
}
let H: ScriptHost;

/* ---------- Zustand ---------- */

let LIST: ScriptListItem[] | null | undefined; // undefined = nicht geladen, null = lädt
let LIST_ERR = "";
const LF = { status: "" as "" | ScriptStatus, client: "" };

interface Editor {
  id: string;
  s: ScriptFull | null;
  err: string;
  base: string;
  dirty: boolean;
  saving: boolean;
  again: boolean;
  timer: number;
  savedAt: number;
  conflict: boolean;
  versions: ScriptVersionRow[] | null | undefined;
  /** gewählter Teil: «brief», «shots» oder die ID eines Videos */
  sel: string;
  fold: Set<string>;
  /** aufgeklappte Zusatzfelder (Regie-Notiz) */
  open: Set<string>;
}
let ED: Editor | null = null;

const TYPE_ICON: Record<ScriptType, string> = { social: "phone", image: "film", interview: "mic", event: "spark" };
const STATUS_CHIP: Record<ScriptStatus, string> = { draft: "", review: "warn", approved: "ok" };
const SETTINGS = ["Innen · Tag", "Aussen · Tag", "Innen · Nacht", "Aussen · Nacht", "Studio", "Screen / Grafik"];
export function initScripts(host: ScriptHost) {
  H = host;
}

const clientName = (id: string | null) => (id ? (H.clients().find((c) => c.id === id)?.name ?? "") : "");
const failMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Unbekannter Fehler");
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.round(h / 24);
  return d < 8 ? `vor ${d} Tag${d === 1 ? "" : "en"}` : fmtDate(iso);
};

/* ============ Liste ============ */

async function loadList() {
  LIST = null;
  try {
    LIST = (await api.scripts()).scripts;
    LIST_ERR = "";
  } catch (e) {
    if (H.authLost(e)) return;
    LIST = [];
    LIST_ERR = failMsg(e);
  }
  if (location.hash.startsWith("#skripte")) H.rerender();
}

function meter(seconds: number, target: number) {
  if (!target) return seconds ? `<span class="muted">${fmtSeconds(seconds)}</span>` : "";
  const pct = Math.min(100, Math.round((seconds / target) * 100));
  const tone = seconds > target * 1.1 ? "bad" : seconds >= target * 0.8 ? "ok" : "";
  return `<span class="sk-mini ${tone}" title="${fmtSeconds(seconds)} von ${fmtSeconds(target)}"><i style="width:${pct}%"></i></span><span class="tnum muted">${fmtSeconds(seconds)} / ${fmtSeconds(target)}</span>`;
}

export function vSkripte(): string {
  if (LIST === undefined) void loadList();
  const head = H.pageHead("Arbeit", "Skripte", "Drehbücher schreiben, mit Kunden abstimmen und als PDF ausgeben.", `<button class="btn" data-s="import" title="Skript aus einer Datei (.drehbuch.json) übernehmen">${ic("download", 16)}Importieren</button><button class="btn pri" data-s="new">${ic("plus", 16)}Neues Skript</button>`);
  if (!LIST) return `${head}<div class="loading" style="padding:50px 0">Skripte werden geladen …</div>`;
  const used = [...new Set(LIST.map((s) => s.clientId).filter((x): x is string => !!x))];
  const rows = LIST.filter((s) => (!LF.status || s.status === LF.status) && (!LF.client || (LF.client === "-" ? !s.clientId : s.clientId === LF.client)));
  const counts = (st: string) => LIST!.filter((s) => !st || s.status === st).length;
  const filters = `<div class="sk-filters"><div class="seg">${[["", "Alle"], ...Object.entries(SCRIPT_STATUS)].map(([k, l]) => `<button class="${LF.status === k ? "on" : ""}" data-s="lf-status" data-k="${k}">${l}<span class="dim">${counts(k)}</span></button>`).join("")}</div>${
    used.length ? `<label class="pill-sel"><select data-s="lf-client" aria-label="Kunde"><option value="">Alle Kunden</option>${used.map((id) => `<option value="${esc(id)}" ${LF.client === id ? "selected" : ""}>${esc(clientName(id) || id)}</option>`).join("")}<option value="-" ${LF.client === "-" ? "selected" : ""}>Ohne Kunde</option></select></label>` : ""
  }</div>`;
  if (!LIST.length) {
    return `${head}${LIST_ERR ? `<div class="sk-banner">${esc(LIST_ERR)}</div>` : ""}<div class="sk-types">${(Object.keys(SCRIPT_TYPES) as ScriptType[]).map((t) => typeCard(t, false, "new-type")).join("")}</div>`;
  }
  const cards = rows.length
    ? `<div class="sk-grid">${rows
        .map((s) => {
          const target = parseLength(s.targetLength);
          const pdf = (kind: string) => `/api/scripts/${encodeURIComponent(s.id)}/pdf?kind=${kind}&download=1`;
          return `<div class="sk-cardw"><a class="sk-card" href="#skript-${esc(s.id)}"><div class="sk-card-top"><span class="sk-type">${ic(TYPE_ICON[s.type], 15)}${SCRIPT_TYPES[s.type].label}</span><span class="chip ${STATUS_CHIP[s.status]}">${SCRIPT_STATUS[s.status]}</span></div><b class="sk-card-title">${esc(s.title)}</b><span class="muted small">${esc(clientName(s.clientId) || "Ohne Kunde")} · ${s.videos > 1 ? `${s.videos} Videos · ` : ""}${s.shots} Aufnahme${s.shots === 1 ? "" : "n"} · v${s.version}</span><div class="sk-card-foot">${meter(s.seconds, target)}<span class="dim small">${ago(s.updatedAt)}</span></div></a>
            <div class="sk-cardacts"><a class="ibtn outline sm" href="${pdf("script")}" title="Drehbuch als PDF" aria-label="Drehbuch als PDF">${ic("download", 15)}</a>
            <details class="menu"><summary class="ibtn outline sm" aria-label="Weitere Aktionen">${ic("more", 15)}</summary><div class="menu-list">
              <a class="mi" href="#skript-${esc(s.id)}">${ic("edit", 16)} Bearbeiten</a><a class="mi" href="${pdf("shotlist")}">${ic("download", 16)} Drehliste als PDF</a><hr>
              ${(Object.entries(SCRIPT_STATUS) as [ScriptStatus, string][]).map(([k, l]) => `<button data-s="c-status" data-id="${esc(s.id)}" data-k="${k}" ${k === s.status ? "disabled" : ""}>${ic(k === s.status ? "check" : "arrow", 16)} ${l}</button>`).join("")}<hr>
              <button data-s="c-dup" data-id="${esc(s.id)}">${ic("copy", 16)} Duplizieren</button><button class="dng" data-s="c-del" data-id="${esc(s.id)}">${ic("trash", 16)} Löschen</button></div></details></div></div>`;
        })
        .join("")}</div>`
    : H.emptyBox("Keine Skripte", "Für diesen Filter gibt es nichts.");
  return `${head}${filters}${cards}`;
}

function typeCard(t: ScriptType, on: boolean, act: string) {
  const T = SCRIPT_TYPES[t];
  return `<button type="button" class="sk-typecard ${on ? "on" : ""}" data-s="${act}" data-k="${t}"><span class="sk-typeic">${ic(TYPE_ICON[t], 20)}</span><b>${T.label}</b><span>${T.desc}</span><em>${T.format} · ${T.length}</em></button>`;
}

let NEW = { type: "social" as ScriptType, clientId: "" };
export const openNewScript = (clientId?: string) => newModal(undefined, clientId);
function newModal(type?: ScriptType, clientId?: string) {
  NEW = { type: type ?? NEW.type, clientId: clientId ?? "" };
  const clients = H.clients().filter((c) => c.status !== "archiv");
  H.openModal(
    `${H.mhead("Neues Skript")}<form data-sform="new" class="stack" style="gap:16px">
      <div class="field"><label for="sk-n-title">Titel</label><input class="in" id="sk-n-title" placeholder="z. B. Reel Herbstaktion" maxlength="200" autocomplete="off" required></div>
      <div class="field"><span class="flabel">Videoart</span><div class="sk-types sm">${(Object.keys(SCRIPT_TYPES) as ScriptType[]).map((t) => typeCard(t, t === NEW.type, "new-pick")).join("")}</div></div>
      <div class="form-grid"><div class="field"><label for="sk-n-client">Kunde</label><select class="in" id="sk-n-client" data-s="new-client"><option value="">Ohne Kunde</option>${clients.map((c) => `<option value="${esc(c.id)}" ${c.id === NEW.clientId ? "selected" : ""}>${esc(c.name)}</option>`).join("")}</select></div>
      <div class="field"><label for="sk-n-project">Projekt (optional)</label><select class="in" id="sk-n-project">${projectOptions(NEW.clientId, "")}</select></div></div>
      <p class="help" style="margin:0">Die Vorlage bringt einen passenden Ablauf mit. Alles lässt sich danach frei ändern, auch weitere Videos aus demselben Dreh.</p>
      <div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Erstellen</button></div></form>`,
    "wide",
  );
}
function projectOptions(clientId: string, sel: string) {
  const ps = H.projects().filter((p) => !clientId || p.client === clientId);
  const open = ps.filter((p) => !["Verrechnet", "Abgeschlossen"].includes(p.phase));
  const list = open.length ? open : ps;
  return `<option value="">Kein Projekt</option>${list
    .slice(0, 300)
    .map((p) => `<option value="${esc(p.id)}" ${p.id === sel ? "selected" : ""}>${esc(p.title)}${clientId ? "" : ` · ${esc(clientName(p.client))}`}</option>`)
    .join("")}${sel && !list.some((p) => p.id === sel) ? `<option value="${esc(sel)}" selected>${esc(H.projects().find((p) => p.id === sel)?.title ?? "Projekt")}</option>` : ""}`;
}

async function createFromModal(btn: HTMLButtonElement | null) {
  const title = (document.getElementById("sk-n-title") as HTMLInputElement | null)?.value.trim() ?? "";
  if (!title) return H.toast("Gib dem Skript einen Titel.");
  const clientId = (document.getElementById("sk-n-client") as HTMLSelectElement | null)?.value || null;
  const projectId = (document.getElementById("sk-n-project") as HTMLSelectElement | null)?.value || null;
  if (btn) btn.disabled = true;
  try {
    const { script } = await api.createScript({ title, type: NEW.type, clientId, projectId });
    H.closeModal();
    LIST = undefined;
    ED = { id: script.id, s: script, err: "", base: script.updatedAt, dirty: false, saving: false, again: false, timer: 0, savedAt: Date.now(), conflict: false, versions: [], sel: "brief", fold: new Set(), open: new Set() };
    H.go(`skript-${script.id}`);
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  } finally {
    if (btn) btn.disabled = false;
  }
}

/* ============ Editor ============ */

async function loadScript(id: string) {
  try {
    const { script } = await api.script(id);
    if (ED?.id !== id) return;
    ED.s = script;
    ED.base = script.updatedAt;
    ED.err = "";
  } catch (e) {
    if (H.authLost(e) || ED?.id !== id) return;
    ED.err = failMsg(e);
  }
  if (location.hash === `#skript-${id}`) paint();
}
async function loadVersions() {
  if (!ED) return;
  const id = ED.id;
  ED.versions = null;
  try {
    const { versions } = await api.scriptVersions(id);
    if (ED?.id === id) ED.versions = versions;
  } catch {
    if (ED?.id === id) ED.versions = [];
  }
  const box = document.getElementById("sk-versions");
  if (box && ED?.id === id) box.innerHTML = versionsList();
}

/** Wird von der Seite aufgerufen, wenn die Ansicht wechselt: offene Änderungen sofort sichern. */
export function leaveEditor(nextHash: string) {
  if (ED && nextHash !== `skript-${ED.id}`) {
    if (ED.dirty) void saveNow();
  }
}
export const editorBusy = () => !!ED && !!document.activeElement?.closest(".sk-ed");
export const hasUnsaved = () => !!ED && (ED.dirty || ED.saving);

export function vSkript(id: string): string {
  if (!ED || ED.id !== id) {
    if (ED?.dirty) void saveNow();
    ED = { id, s: null, err: "", base: "", dirty: false, saving: false, again: false, timer: 0, savedAt: 0, conflict: false, versions: undefined, sel: "", fold: new Set(), open: new Set() };
    void loadScript(id);
  }
  const back = `<a href="#skripte" class="sk-back">${ic("left", 16)}Alle Skripte</a>`;
  if (ED.err) return `${back}${H.emptyBox("Skript nicht gefunden", esc(ED.err), `<a class="btn sm" href="#skripte">Zur Liste</a>`)}`;
  if (!ED.s) return `${back}<div class="loading" style="padding:50px 0">Skript wird geladen …</div>`;
  if (ED.versions === undefined) void loadVersions();
  return editorHTML(ED.s);
}

/** Editor neu zeichnen, Scrollposition und (optional) Fokus behalten. */
function paint(focusId?: string) {
  const v = document.getElementById("view");
  if (!v || !ED || !location.hash.startsWith("#skript-")) return;
  const y = window.scrollY;
  const prevY = document.getElementById("sk-pv-scroll")?.scrollTop ?? 0;
  const active = (document.activeElement as HTMLElement | null)?.id;
  v.innerHTML = vSkript(ED.id);
  window.scrollTo(0, y);
  afterPaint();
  const pv = document.getElementById("sk-pv-scroll");
  if (pv) pv.scrollTop = prevY;
  const f = document.getElementById(focusId ?? active ?? "");
  if (f instanceof HTMLTextAreaElement || f instanceof HTMLInputElement) {
    f.focus({ preventScroll: !focusId });
    if (focusId) f.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}
export function afterPaint() {
  document.getElementById("view")?.classList.toggle("wide", location.hash.startsWith("#skript-"));
  document.querySelectorAll<HTMLTextAreaElement>(".sk-ed textarea").forEach(grow);
}
const grow = (t: HTMLTextAreaElement) => {
  t.style.height = "auto";
  t.style.height = `${t.scrollHeight + 2}px`;
};
// Bei geänderter Breite umbrechen die Texte anders: Höhe nachführen
let growTimer = 0;
window.addEventListener("resize", () => {
  clearTimeout(growTimer);
  growTimer = window.setTimeout(() => document.querySelectorAll<HTMLTextAreaElement>(".sk-ed textarea").forEach(grow), 120);
});

const ta = (id: string, path: string, value: string, ph: string, cls = "", rows = 1) =>
  `<textarea class="in sk-ta ${cls}" id="${id}" data-s="f" data-p="${path}" rows="${rows}" placeholder="${esc(ph)}" spellcheck="true">${esc(value)}</textarea>`;
const inp = (id: string, path: string, value: string, ph: string, cls = "", extra = "") =>
  `<input class="in ${cls}" id="${id}" data-s="f" data-p="${path}" value="${esc(value)}" placeholder="${esc(ph)}" autocomplete="off" ${extra}>`;
const tool = (act: string, ids: Record<string, string>, icon: string, label: string, disabled = false) =>
  `<button type="button" class="ibtn sm" data-s="${act}" ${Object.entries(ids)
    .map(([k, v]) => `data-${k}="${esc(v)}"`)
    .join(" ")} title="${label}" aria-label="${label}" ${disabled ? "disabled" : ""}>${ic(icon, 15)}</button>`;

/* ---------- Aufbau ---------- */

const videoById = (vid: string) => ED?.s?.content.videos.find((v) => v.id === vid);
const beatById = (vid: string, bid: string) => videoById(vid)?.beats.find((x) => x.id === bid);
function currentSel(c: ScriptContent) {
  const ed = ED!;
  if (ed.sel === "brief" || ed.sel === "shots" || c.videos.some((v) => v.id === ed.sel)) return ed.sel;
  const filled = c.videos[0]?.beats.some((b) => b.visual.trim() || parseSound(b.audio).text.replace(/\[[^\]]*\]/g, "").trim());
  ed.sel = filled ? c.videos[0]!.id : "brief";
  return ed.sel;
}
const runLabel = (secs: number, target: number) => `${fmtTc(secs)}${target ? ` / ${fmtTc(target)}` : ""}`;
const tone = (secs: number, target: number) => (!target ? "" : secs > target * 1.1 ? "bad" : secs >= target * 0.8 ? "ok" : "");
/** Zeile mit offenen Details (nur eine gleichzeitig) */
let DETAIL = "";
const readPrev = () => {
  try {
    return localStorage.getItem("sk-prev") === "1";
  } catch {
    return false;
  }
};
/** Vorschau rechts (auf schmalen Bildschirmen anstelle des Editors) */
let PREV = readPrev();

function editorHTML(s: ScriptFull): string {
  const c = s.content;
  const ed = ED!;
  const sel = currentSel(c);
  const clients = H.clients().filter((x) => x.status !== "archiv" || x.id === s.clientId);
  const head = `<header class="sk-top">
    <div class="sk-topline">
      <a href="#skripte" class="sk-back">${ic("left", 16)}Skripte</a>
      <span class="dim small" id="sk-save">${saveLabel()}</span>
      <span class="sk-grow"></span>
      <button class="btn ghost sm sk-prevbtn ${PREV ? "on" : ""}" data-s="prev-toggle" title="Vorschau ein-/ausblenden">${ic("eye", 15)} Vorschau</button>
      <label class="pill-sel tone-${s.status === "approved" ? "done" : s.status === "review" ? "review" : "open"}"><i class="tone-dot"></i><select data-s="status" aria-label="Status">${(Object.entries(SCRIPT_STATUS) as [ScriptStatus, string][]).map(([k, l]) => `<option value="${k}" ${k === s.status ? "selected" : ""}>${l}</option>`).join("")}</select>${ic("chevd", 13)}</label>
      <details class="menu"><summary class="btn pri sm">${ic("download", 15)} PDF ${ic("chevd", 13)}</summary><div class="menu-list">
        <button data-s="pdf-view" data-k="script">${ic("ext", 16)} Drehbuch ansehen</button>
        <button data-s="pdf" data-k="script">${ic("download", 16)} Drehbuch herunterladen</button>
        <button data-s="pdf" data-k="shotlist">${ic("download", 16)} Drehliste herunterladen</button></div></details>
      <details class="menu"><summary class="ibtn outline sm" aria-label="Weitere Aktionen">${ic("more", 16)}</summary><div class="menu-list">
        <button data-s="prompter">${ic("play", 16)} Teleprompter</button><hr>
        <button data-s="versions">${ic("clock", 16)} Versionen${ed.versions?.length ? ` (${ed.versions.length})` : ""}</button>
        <button data-s="freeze">${ic("check", 16)} Version ${s.version} abschliessen</button><hr>
        <button data-s="dup">${ic("copy", 16)} Duplizieren</button><button data-s="export">${ic("download", 16)} Als Datei sichern</button><button class="dng" data-s="del">${ic("trash", 16)} Löschen</button></div></details>
    </div>
    <input class="sk-title" id="sk-title" data-s="meta" data-p="title" value="${esc(s.title)}" maxlength="200" aria-label="Titel" autocomplete="off">
    <div class="sk-metarow">
      <label class="pill-sel ghost">${ic("users", 13)}<select data-s="meta" data-p="clientId" aria-label="Kunde"><option value="">Ohne Kunde</option>${clients.map((x) => `<option value="${esc(x.id)}" ${x.id === s.clientId ? "selected" : ""}>${esc(x.name)}</option>`).join("")}</select>${ic("chevd", 13)}</label>
      <label class="pill-sel ghost">${ic("board", 13)}<select data-s="meta" data-p="projectId" aria-label="Projekt">${projectOptions(s.clientId ?? "", s.projectId ?? "")}</select>${ic("chevd", 13)}</label>
      <span class="sk-total" id="sk-total">${totalLabel(c)}</span>
    </div>
  </header>`;
  const conflict = ed.conflict
    ? `<div class="sk-banner sk-conflict"><span>Dieses Skript wurde inzwischen an einem anderen Ort geändert. Deine letzten Eingaben sind nicht gespeichert.</span><button class="btn sm" data-s="reload">Neu laden</button></div>`
    : "";
  const video = c.videos.find((v) => v.id === sel);
  const main = sel === "brief" ? briefHTML(c) : sel === "shots" ? shotsHTML(c) : video ? videoHTML(c, video) : "";
  const locs = [...new Set([...beatsOf(c).map((x) => x.location.trim()), ...fieldValue(c, "locations").split("\n").map((x) => x.trim())].filter(Boolean))];
  const lists = `<datalist id="sk-locs">${locs.map((x) => `<option value="${esc(x)}">`).join("")}</datalist>`;
  const preview = PREV ? `<aside class="sk-prev"><div class="sk-prev-scroll" id="sk-pv-scroll"><div class="pv-paper" id="sk-pv">${previewHTML(s)}</div></div></aside>` : "";
  return `<div class="sk-ed">${lists}${head}${conflict}<nav class="sk-tabs" id="sk-tabs">${tabsHTML(c, sel)}</nav><div class="sk-work ${PREV ? "with-prev" : ""}"><div class="sk-main">${main}</div>${preview}</div></div>`;
}

function totalLabel(c: ScriptContent) {
  const n = c.videos.length;
  const secs = totalSeconds(c);
  const target = targetSeconds(c);
  return `${n > 1 ? `${n} Videos · ` : ""}<span class="tnum ${tone(secs, target)}">${runLabel(secs, target)}</span>`;
}

function tabsHTML(c: ScriptContent, sel: string) {
  const tab = (k: string, label: string, sub = "", del = "") => `<div class="sk-tab ${sel === k ? "on" : ""}" data-s="sel" data-k="${esc(k)}" role="tab" tabindex="0"><b>${label}</b>${sub ? `<small>${sub}</small>` : ""}${del}</div>`;
  const many = c.videos.length > 1;
  return `${tab("brief", "Grundlage")}${c.videos
    .map((v, i) =>
      tab(
        v.id,
        `${many ? `<i>${i + 1}</i>` : ""}${esc(v.title.trim() || (many ? `Video ${i + 1}` : "Video"))}`,
        `<span class="tnum ${tone(videoSeconds(v), parseLength(v.targetLength))}">${fmtTc(videoSeconds(v))}</span>`,
        many ? `<button class="sk-tab-x" data-s="video-del" data-vid="${v.id}" title="Video löschen" aria-label="Video löschen">${ic("x", 13)}</button>` : "",
      ),
    )
    .join("")}<button class="sk-tab add" data-s="video-add" title="Weiteres Video aus demselben Dreh">${ic("plus", 14)}Video</button><span class="sk-grow"></span>${tab("shots", `${ic("check", 14)} Drehliste`)}`;
}

/* ---------- Grundlage: Briefing und Drehtag ---------- */

function fieldHTML(grp: "fb" | "fp", f: ScriptField) {
  const fixed = (grp === "fb" ? BRIEF_KEYS : PRODUCTION_KEYS).includes(f.key);
  const ph = FIELD_KEYS[f.key]?.ph ?? "";
  const label = fixed
    ? `<label class="sk-f-label" for="f-${f.id}-value">${esc(f.label)}</label>`
    : `<div class="sk-f-top"><input class="sk-f-label" id="f-${f.id}-label" data-s="f" data-p="${grp}.${f.id}.label" value="${esc(f.label)}" placeholder="Bezeichnung" aria-label="Bezeichnung" autocomplete="off"><span class="sk-tools sk-hover">${tool("f-del", { grp, fid: f.id }, "x", "Angabe entfernen")}</span></div>`;
  const input =
    f.key === "shootDate"
      ? `<input class="in" type="date" id="f-${f.id}-value" data-s="f" data-p="${grp}.${f.id}.value" value="${esc(/^\d{4}-\d{2}-\d{2}$/.test(f.value) ? f.value : "")}">${f.value && !/^\d{4}-\d{2}-\d{2}$/.test(f.value) ? `<small class="dim">bisher: ${esc(f.value)}</small>` : ""}`
      : ta(`f-${f.id}-value`, `${grp}.${f.id}.value`, f.value, ph, "", f.long ? 2 : 1);
  return `<div class="sk-f ${f.long ? "long" : ""}">${label}${input}</div>`;
}
function briefHTML(c: ScriptContent) {
  const card = (grp: "fb" | "fp", title: string, sub: string, fields: ScriptField[]) =>
    `<section class="sk-card"><div class="sk-card-h"><h2>${title}</h2><span>${sub}</span></div><div class="sk-fields">${fields.map((f) => fieldHTML(grp, f)).join("")}</div></section>`;
  return `<div class="sk-brief">${card("fb", "Briefing", c.videos.length > 1 ? `Gilt für alle ${c.videos.length} Videos` : "Kurz und klar", c.brief)}${card("fp", "Drehtag", "Erscheint auch auf der Drehliste", c.production)}</div>`;
}

/* ---------- Ein Video: Tabelle Zeit | Bild | Ton ---------- */

function videoHTML(c: ScriptContent, v: ScriptVideo) {
  const i = c.videos.indexOf(v);
  const n = c.videos.length;
  const secs = videoSeconds(v);
  const target = parseLength(v.targetLength);
  const pct = target ? Math.min(100, (secs / target) * 100) : 0;
  const tcs = timecodes(v);
  const places = placesOf(v);
  const head = `<section class="sk-vhead">
    ${inp(`v-${v.id}-title`, `v.${v.id}.title`, v.title, "Titel des Videos", "sk-vtitle")}
    <div class="sk-vline">
      <label class="pill-sel ghost">${ic("film", 13)}<select data-s="f" data-p="v.${v.id}.format" aria-label="Format">${[...new Set([...FORMATS, v.format])].map((f) => `<option ${f === v.format ? "selected" : ""}>${esc(f)}</option>`).join("")}</select>${ic("chevd", 13)}</label>
      <span class="sk-meter ${tone(secs, target)}" id="sk-vrun-${v.id}" title="Summe der Zeilen; ohne Angabe geschätzt"><b class="tnum">${fmtTc(secs)}</b><span class="sk-mbar"><i style="width:${pct}%"></i></span><span class="dim">von</span><input class="sk-target" id="v-${v.id}-len" data-s="f" data-p="v.${v.id}.targetLength" value="${esc(v.targetLength)}" placeholder="0:30" aria-label="Ziellänge"></span>
      <span class="sk-grow"></span>
      <details class="menu"><summary class="ibtn sm" aria-label="Video bearbeiten">${ic("more", 16)}</summary><div class="menu-list">
        ${i > 0 ? `<button data-s="video-up" data-vid="${v.id}">${ic("left", 16)} Nach vorne</button>` : ""}${i < n - 1 ? `<button data-s="video-down" data-vid="${v.id}">${ic("chev", 16)} Nach hinten</button>` : ""}
        <button data-s="video-dup" data-vid="${v.id}">${ic("copy", 16)} Duplizieren</button><button data-s="q-add" data-vid="${v.id}">${ic("mic", 16)} Interviewfragen</button>${n > 1 ? `<hr><button class="dng" data-s="video-del" data-vid="${v.id}">${ic("trash", 16)} Video löschen</button>` : ""}</div></details>
    </div>
    ${ta(`v-${v.id}-idea`, `v.${v.id}.idea`, v.idea, "Idee in einem Satz", "sk-idea")}
  </section>`;
  const rows = v.beats
    .map((b, bi) => {
      const place = places[bi] ?? "";
      const slug = place && place !== (places[bi - 1] ?? "") ? `<div class="sx-place">${ic("pin", 13)}${esc(place)}</div>` : "";
      return slug + rowHTML(v, b, bi, tcs[bi] ?? 0, place);
    })
    .join("");
  const table = `<section class="sx">
    <div class="sx-th"><span>Zeit</span><span>Bild</span><span>Ton</span><span></span></div>
    ${rows || `<div class="empty"><b>Noch leer</b><span>Füge die erste Zeile hinzu.</span></div>`}
    <div class="sx-foot"><button class="btn sm" data-s="beat-add" data-vid="${v.id}">${ic("plus", 15)} Zeile</button><span class="dim small">Im Ton: <code>[Musik]</code> · <code>Name: Aussage</code> für ein Statement · <code>Sprecher: Text</code> für die Off-Stimme</span></div>
  </section>`;
  const qs = v.questions.length
    ? `<section class="sk-card"><div class="sk-card-h"><h2>Fragen</h2><span>${v.questions.length}</span></div><div class="sk-qs">${v.questions
        .map(
          (q, qi) =>
            `<div class="sk-q"><span class="sk-qn">${qi + 1}</span><div class="sk-qbody">${ta(`q-${q.id}-text`, `q.${v.id}.${q.id}.text`, q.text, "Frage")}</div><div class="sk-tools sk-hover">${tool("q-up", { vid: v.id, qid: q.id }, "up", "Nach oben", qi === 0)}${tool("q-down", { vid: v.id, qid: q.id }, "down", "Nach unten", qi === v.questions.length - 1)}${tool("q-del", { vid: v.id, qid: q.id }, "trash", "Frage löschen")}</div></div>`,
        )
        .join("")}</div><button class="sk-link" data-s="q-add" data-vid="${v.id}">${ic("plus", 14)}Frage</button></section>`
    : "";
  return `${head}${table}${qs}`;
}

/** Kurzinfo unter dem Bild: Einstellung, Text im Bild, Notiz */
function picSub(b: ScriptBeat) {
  const parts = [b.shot ? `<span>${esc(b.shot)}</span>` : "", b.overlay ? `<span>Text im Bild: <b>${esc(b.overlay)}</b></span>` : "", b.note ? `<span class="sx-note">${esc(b.note)}</span>` : ""].filter(Boolean);
  return parts.length ? `<div class="sx-sub" data-s="detail" data-vid="" data-bid="${b.id}">${parts.join("")}</div>` : "";
}
const KIND_TAG: Record<string, string> = { statement: "Statement", sprecher: "Sprecher", bild: "" };

function rowHTML(v: ScriptVideo, b: ScriptBeat, bi: number, tc: number, place: string) {
  const p = `bt.${v.id}.${b.id}`;
  const ids = { vid: v.id, bid: b.id };
  const open = DETAIL === b.id;
  const d = beatSeconds(b);
  const opts = b.options.filter((o) => o.trim());
  const optList = opts.length && !open ? `<ol class="sx-opts">${opts.map((o) => `<li>${esc(o)}</li>`).join("")}</ol>` : "";
  const shotOpts = [...new Set([...SHOT_SIZES, b.shot].filter(Boolean))];
  const details = open
    ? `<div class="sx-detail">
        <label><span>Einstellung</span><select class="in sm" data-s="f" data-p="${p}.shot"><option value="">–</option>${shotOpts.map((x) => `<option ${x === b.shot ? "selected" : ""}>${esc(x)}</option>`).join("")}</select></label>
        <label><span>Ort ab hier</span><input class="in sm" id="bt-${b.id}-loc" data-s="f" data-p="${p}.location" value="${esc(b.location)}" placeholder="${esc(place || "z. B. Showroom")}" list="sk-locs" autocomplete="off"></label>
        <label><span>Dauer (Sek.)</span><input class="in sm tnum" id="bt-${b.id}-dur" data-s="f" data-p="${p}.duration" value="${esc(b.duration)}" placeholder="~${beatSeconds({ ...b, duration: "" })}" inputmode="decimal"></label>
        <label class="wide"><span>Text im Bild</span><input class="in sm" id="bt-${b.id}-ov" data-s="f" data-p="${p}.overlay" value="${esc(b.overlay)}" placeholder="z. B. Name und Funktion, Logo" autocomplete="off"></label>
        <label class="wide"><span>Notiz</span><input class="in sm" id="bt-${b.id}-note" data-s="f" data-p="${p}.note" value="${esc(b.note)}" placeholder="Regie-Hinweis" autocomplete="off"></label>
        <div class="wide sx-alt"><span>Andere Sätze zur Auswahl</span>${b.options
          .map((o, oi) => `<div class="sx-alt-r"><i>${oi + 1}</i>${ta(`bt-o-${b.id}-${oi}`, `o.${v.id}.${b.id}.${oi}`, o, "Alternative")}${tool("opt-use", { ...ids, k: String(oi) }, "up", "Als Hauptsatz übernehmen")}${tool("opt-del", { ...ids, k: String(oi) }, "x", "Entfernen")}</div>`)
          .join("")}<button class="sk-link sm" data-s="opt-add" data-vid="${v.id}" data-bid="${b.id}">${ic("plus", 12)}Satz</button></div>
        <div class="wide sx-detail-foot"><button class="btn sm" data-s="detail" data-vid="${v.id}" data-bid="${b.id}">Fertig</button></div>
      </div>`
    : "";
  return `<div class="sx-row k-${b.kind} ${open ? "open" : ""}" data-beat="${b.id}" data-vid="${v.id}" id="sx-${b.id}">
    <div class="sx-tc"><b class="tnum" id="bt-tc-${b.id}">${fmtTc(tc)}</b><i id="bt-d-${b.id}">${beatIsEstimated(b) ? "~" : ""}${d}s</i><em id="bt-k-${b.id}">${KIND_TAG[b.kind]}</em></div>
    <div class="sx-cell">${ta(`bt-${b.id}-visual`, `${p}.visual`, b.visual, "Was sieht man?")}${open ? "" : picSub(b)}</div>
    <div class="sx-cell">${ta(`bt-${b.id}-audio`, `${p}.audio`, b.audio, "[Musik] · Name: Aussage · Sprecher: Text")}${optList}</div>
    <div class="sx-act"><button class="ibtn sm ${open ? "on" : ""}" data-s="detail" data-vid="${v.id}" data-bid="${b.id}" title="Details: Einstellung, Ort, Dauer, Text im Bild, Notiz, andere Sätze" aria-label="Details">${ic("more", 16)}</button><span class="sk-hover sx-move">${tool("beat-up", ids, "up", "Nach oben", bi === 0)}${tool("beat-down", ids, "down", "Nach unten", bi === v.beats.length - 1)}${tool("beat-del", ids, "trash", "Zeile löschen")}</span></div>
    ${details}
  </div>`;
}

/* ---------- Vorschau (gleicher Aufbau wie das PDF) ---------- */

const nl = (t: string) => esc(t).replace(/\n/g, "<br>");
/** «2026-10-30» → «Fr 30.10.2026» (wie im PDF) */
const dayCh = (s: string) => {
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s.trim();
  const wd = new Date(`${m[0]}T12:00:00Z`).toLocaleDateString("de-CH", { weekday: "short", timeZone: "UTC" }).replace(".", "");
  return `${wd} ${m[3]}.${m[2]}.${m[1]}`;
};
function previewHTML(s: ScriptFull): string {
  const c = s.content;
  const client = H.clients().find((x) => x.id === s.clientId)?.name ?? "";
  const date = dayCh(fieldValue(c, "shootDate"));
  const facts = [client, date ? `Dreh ${date}` : "", c.videos.length > 1 ? `${c.videos.length} Videos` : "", [...new Set(c.videos.map((v) => v.format))].join(" / "), `Version ${s.version} · ${SCRIPT_STATUS[s.status]}`].filter(Boolean);
  const grid = (fields: ScriptField[]) =>
    fields
      .filter((f) => f.value.trim())
      .map((f) => `<div class="pv-f"><span>${esc(f.label)}</span><p>${nl(f.key === "shootDate" ? dayCh(f.value) : f.value)}</p></div>`)
      .join("");
  const b = grid(c.brief);
  const p = grid(c.production);
  const videos = c.videos
    .map((v, vi) => {
      const tcs = timecodes(v);
      const places = placesOf(v);
      const rows = v.beats
        .map((x, i) => {
          const place = places[i] ?? "";
          const slug = place && place !== (places[i - 1] ?? "") ? `<div class="pv-place">${esc(place)}</div>` : "";
          const snd = parseSound(x.audio);
          const opts = x.options.filter((o) => o.trim());
          const sound =
            snd.kind === "statement"
              ? `<b class="pv-who">${esc(snd.who)}</b>${snd.text ? `«${nl(snd.text)}»` : "–"}`
              : snd.kind === "sprecher"
                ? `<b class="pv-who">Sprecher</b>${nl(snd.text) || "–"}`
                : `<span class="pv-grey">${nl(snd.text)}</span>`;
          const sub = [x.shot ? `<em>${esc(x.shot)}</em>` : "", x.overlay ? `<em>Text im Bild: <b>${esc(x.overlay)}</b></em>` : "", x.note ? `<em class="pv-note">${esc(x.note)}</em>` : ""].join("");
          return `${slug}<div class="pv-bt"><span class="pv-tc"><b>${fmtTc(tcs[i] ?? 0)}</b><i>${beatIsEstimated(x) ? "~" : ""}${beatSeconds(x)}s</i></span><div><p>${nl(x.visual) || "–"}</p>${sub}</div><div>${sound}${opts.length ? `<div class="pv-opts"><i>oder</i>${opts.map((o, oi) => `<span><b>${oi + 1}</b>${esc(o)}</span>`).join("")}</div>` : ""}</div></div>`;
        })
        .join("");
      return `<div class="pv-video" data-s="pv-jump" data-k="${v.id}"><div class="pv-vh">${c.videos.length > 1 ? `<span>Video ${vi + 1}</span>` : ""}<b>${esc(videoLabel(c, v))}</b><em>${esc([v.format, runLabel(videoSeconds(v), parseLength(v.targetLength))].join(" · "))}</em></div>${v.idea ? `<p class="pv-idea">${nl(v.idea)}</p>` : ""}${rows ? `<div class="pv-bt pv-bth"><span>Zeit</span><span>Bild</span><span>Ton</span></div>${rows}` : ""}</div>`;
    })
    .join("");
  const logo = document.querySelector(".brand svg")?.outerHTML ?? "";
  return `<div class="pv-logo">${logo}</div><div class="pv-kicker"><span>Drehbuch</span></div><h1 class="pv-title">${esc(s.title)}</h1><div class="pv-sub">${esc(facts.join("  ·  "))}</div>
    ${b || p ? `<div class="pv-two" data-s="pv-jump" data-k="brief">${b ? `<div><h4>Briefing</h4>${b}</div>` : "<div></div>"}${p ? `<div><h4>Drehtag</h4>${p}</div>` : ""}</div>` : ""}
    ${videos}`;
}
let pvTimer = 0;
function refreshPreview(now = false) {
  clearTimeout(pvTimer);
  const run = () => {
    const pv = document.getElementById("sk-pv");
    if (pv && ED?.s) pv.innerHTML = previewHTML(ED.s);
  };
  if (now) run();
  else pvTimer = window.setTimeout(run, 160);
}

/* ---------- Drehliste (automatisch) ---------- */

function shotsHTML(c: ScriptContent) {
  const groups = planByLocation(c);
  const total = shotCount(c);
  const body = groups.size
    ? [...groups]
        .map(
          ([loc, items]) => `<div class="sk-sl"><div class="sk-sl-h">${ic("pin", 14)}<b>${esc(loc)}</b><span>${items.length}</span></div>${items
            .map(({ video, beat: b, tc }) => {
              const snd = parseSound(b.audio);
              return `<div class="sk-sl-r"><span class="sk-sl-n tnum">${esc(video ? `${video} · ${fmtTc(tc)}` : fmtTc(tc))}</span><span class="sk-sl-shot">${esc(b.shot || "")}</span><span class="min0"><span class="ellip">${esc(b.visual || "–")}</span>${snd.kind === "statement" ? `<small><b>${esc(snd.who)}:</b> «${esc(snd.text)}»${b.options.length ? ` · oder ${b.options.length} weitere Sätze` : ""}</small>` : ""}</span></div>`;
            })
            .join("")}</div>`,
        )
        .join("")
    : `<div class="empty"><b>Noch keine Aufnahmen</b><span>Sobald die Videos Zeilen haben, erscheinen sie hier.</span></div>`;
  return `<section class="sk-card"><div class="sk-card-h"><h2>Drehliste</h2><span>${total} Aufnahme${total === 1 ? "" : "n"}</span></div>
    <p class="muted small" style="margin:-4px 0 10px">${c.videos.length > 1 ? `Alle Aufnahmen aus ${c.videos.length} Videos, ` : "Alle Aufnahmen, "}nach Ort sortiert. So drehst du am Drehtag.</p>
    ${body}<div class="sk-flow-foot"><button class="btn sm" data-s="pdf" data-k="shotlist">${ic("download", 15)} Drehliste als PDF</button></div></section>`;
}

function versionsList() {
  const v = ED?.versions;
  if (v === null || v === undefined) return `<span class="dim small">Lädt …</span>`;
  if (!v.length) return `<p class="muted small" style="margin:0">Noch keine abgeschlossene Version. Schliesse eine Version ab, bevor du sie dem Kunden schickst. Danach arbeitest du an der nächsten Nummer weiter, die alte bleibt als PDF abrufbar.</p>`;
  return `<ul class="sk-vlist">${v
    .map(
      (x) => `<li><span class="sk-vn">v${x.version}</span><span class="min0"><span class="ellip">${esc(x.note || "Ohne Notiz")}</span><span class="dim small">${fmtDate(x.createdAt)}</span></span>
      <span class="sk-tools">${tool("v-pdf", { id: x.id }, "download", `Drehbuch v${x.version} als PDF`)}${tool("v-restore", { id: x.id }, "refresh", `Inhalt von v${x.version} zurückholen`)}</span></li>`,
    )
    .join("")}</ul>`;
}
function versionsModal() {
  if (!ED?.s) return;
  H.openModal(`${H.mhead("Versionen")}<div id="sk-versions">${versionsList()}</div><div class="modal-foot"><button class="btn ghost" data-a="close">Schliessen</button><button class="btn pri" data-s="freeze">v${ED.s.version} abschliessen</button></div>`);
  if (ED.versions === undefined) void loadVersions();
}

function saveLabel() {
  if (!ED) return "";
  if (ED.conflict) return "Nicht gespeichert";
  if (ED.saving || ED.dirty) return "Speichert …";
  return ED.savedAt ? "Gespeichert" : "";
}
const setSaveLabel = () => {
  const el = document.getElementById("sk-save");
  if (el) el.textContent = saveLabel();
};

/* ---------- Speichern ---------- */

function scheduleSave(ms = 900) {
  if (!ED?.s || ED.conflict) return;
  ED.dirty = true;
  setSaveLabel();
  clearTimeout(ED.timer);
  ED.timer = window.setTimeout(() => void saveNow(), ms);
}
async function saveNow() {
  const ed = ED;
  if (!ed?.s || ed.conflict) return;
  clearTimeout(ed.timer);
  if (ed.saving) {
    ed.again = true;
    return;
  }
  if (!ed.dirty) return;
  ed.dirty = false;
  ed.saving = true;
  setSaveLabel();
  const s = ed.s;
  try {
    const { script } = await api.saveScript(ed.id, { title: s.title, status: s.status, clientId: s.clientId, projectId: s.projectId, content: s.content, baseUpdatedAt: ed.base });
    ed.base = script.updatedAt;
    ed.savedAt = Date.now();
    s.updatedAt = script.updatedAt;
    LIST = undefined;
  } catch (e) {
    if (H.authLost(e)) return;
    if (e instanceof ApiError && e.code === "conflict") {
      ed.conflict = true;
      if (ED === ed) paint();
    } else {
      ed.dirty = true;
      H.toast(`Speichern fehlgeschlagen: ${failMsg(e)}`);
      clearTimeout(ed.timer);
      ed.timer = window.setTimeout(() => void saveNow(), 5000);
    }
  } finally {
    ed.saving = false;
    if (ED === ed) setSaveLabel();
    if (ed.again) {
      ed.again = false;
      void saveNow();
    }
  }
}
window.addEventListener("beforeunload", (e) => {
  if (hasUnsaved()) {
    void saveNow();
    e.preventDefault();
  }
});

/* ---------- Modell ändern ---------- */

function move<T>(arr: T[], i: number, d: number) {
  const j = i + d;
  if (i < 0 || j < 0 || j >= arr.length) return;
  [arr[i], arr[j]] = [arr[j]!, arr[i]!];
}
const BEAT_FIELDS = ["visual", "audio", "shot", "location", "duration", "overlay", "note"] as const;

function setField(path: string, value: string) {
  const c = ED?.s?.content;
  if (!c) return;
  const [a, b, x, y] = path.split(".");
  if ((a === "fb" || a === "fp") && b && x) {
    const f = (a === "fb" ? c.brief : c.production).find((o) => o.id === b);
    if (f && (x === "label" || x === "value")) f[x] = value;
  } else if (a === "v" && b && x) {
    const v = videoById(b);
    if (v && (x === "title" || x === "format" || x === "targetLength" || x === "idea")) v[x] = value;
  } else if (a === "bt" && b && x && y) {
    const bt = beatById(b, x);
    const k = y as (typeof BEAT_FIELDS)[number];
    if (!bt || !BEAT_FIELDS.includes(k)) return;
    bt[k] = value;
    if (k === "audio") syncBeat(bt);
  } else if (a === "o" && b && x && y) {
    const bt = beatById(b, x);
    const i = Number(y);
    if (bt && i >= 0 && i < bt.options.length) bt.options[i] = value;
  } else if (a === "q" && b && x && y) {
    const q = videoById(b)?.questions.find((o) => o.id === x);
    if (q && (y === "text" || y === "note")) q[y] = value;
  }
}

/** Zeiten, Art und Reiter nach einer Eingabe auffrischen, ohne neu zu zeichnen. */
function afterInput(path: string) {
  const s = ED?.s;
  if (!s) return;
  const c = s.content;
  const [a, b, x, y] = path.split(".");
  const v = a === "v" || a === "bt" || a === "q" || a === "o" ? videoById(b ?? "") : undefined;
  if (v) {
    const tcs = timecodes(v);
    v.beats.forEach((bt, i) => {
      const el = document.getElementById(`bt-tc-${bt.id}`);
      if (el) el.textContent = fmtTc(tcs[i] ?? 0);
      const d = document.getElementById(`bt-d-${bt.id}`);
      if (d) d.textContent = `${beatIsEstimated(bt) ? "~" : ""}${beatSeconds(bt)}s`;
    });
    if (a === "bt" && x && y === "audio") {
      const bt = beatById(v.id, x);
      const row = document.getElementById(`sx-${x}`);
      if (bt && row) {
        row.classList.remove("k-bild", "k-statement", "k-sprecher");
        row.classList.add(`k-${bt.kind}`);
        const k = document.getElementById(`bt-k-${x}`);
        if (k) k.textContent = KIND_TAG[bt.kind] ?? "";
      }
    }
    const secs = videoSeconds(v);
    const target = parseLength(v.targetLength);
    const m = document.getElementById(`sk-vrun-${v.id}`);
    if (m) {
      m.className = `sk-meter ${tone(secs, target)}`;
      m.querySelector("b")!.textContent = fmtTc(secs);
      (m.querySelector(".sk-mbar i") as HTMLElement).style.width = `${target ? Math.min(100, (secs / target) * 100) : 0}%`;
    }
  }
  const tabs = document.getElementById("sk-tabs");
  if (tabs && (a === "v" || a === "bt")) tabs.innerHTML = tabsHTML(c, currentSel(c));
  const tot = document.getElementById("sk-total");
  if (tot) tot.innerHTML = totalLabel(c);
  refreshPreview();
}

function structural(act: string, el: HTMLElement) {
  const s = ED?.s;
  if (!s || !ED) return;
  const c = s.content;
  const d = el.dataset;
  const v = d.vid ? videoById(d.vid) : undefined;
  let focus: string | undefined;
  const confirmDel = (text: string, fn: () => void) =>
    H.confirm(text, "Löschen", () => {
      H.closeModal();
      fn();
      scheduleSave(200);
      paint();
    });
  switch (act) {
    case "prev-toggle":
      PREV = !PREV;
      try {
        localStorage.setItem("sk-prev", PREV ? "1" : "0");
      } catch {
        /* privater Modus */
      }
      paint();
      return;
    case "sel":
      ED.sel = d.k ?? "brief";
      DETAIL = "";
      paint();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    case "detail": {
      // Klick auf die Kurzinfo öffnet ebenfalls die Details
      const bid = d.bid ?? "";
      DETAIL = DETAIL === bid ? "" : bid;
      paint(DETAIL ? `bt-${bid}-loc` : undefined);
      return;
    }
    case "video-add": {
      const last = c.videos.at(-1);
      const nv = videoTemplate(s.type, { format: last?.format ?? SCRIPT_TYPES[s.type].format, targetLength: last?.targetLength ?? SCRIPT_TYPES[s.type].length });
      nv.questions = [];
      c.videos.push(nv);
      ED.sel = nv.id;
      focus = `v-${nv.id}-title`;
      break;
    }
    case "video-up":
    case "video-down":
      if (!v) return;
      move(c.videos, c.videos.indexOf(v), act === "video-up" ? -1 : 1);
      break;
    case "video-dup": {
      if (!v) return;
      const copy: ScriptVideo = structuredClone(v);
      copy.id = nid();
      copy.title = v.title ? `${v.title} (Kopie)` : "";
      for (const bt of copy.beats) bt.id = nid();
      for (const q of copy.questions) q.id = nid();
      c.videos.splice(c.videos.indexOf(v) + 1, 0, copy);
      ED.sel = copy.id;
      focus = `v-${copy.id}-title`;
      break;
    }
    case "video-del":
      if (!v || c.videos.length < 2) return;
      return confirmDel(`«${esc(videoLabel(c, v))}» mit ${v.beats.length} Zeilen löschen?`, () => {
        c.videos.splice(c.videos.indexOf(v), 1);
        ED!.sel = c.videos[0]!.id;
      });
    case "f-del": {
      const list = d.grp === "fp" ? c.production : c.brief;
      const f = list.find((x) => x.id === d.fid);
      if (!f) return;
      return confirmDel(`Angabe «${esc(f.label || "ohne Namen")}» entfernen?`, () => list.splice(list.indexOf(f), 1));
    }
    case "q-add":
      if (v) {
        const q = newQuestion();
        v.questions.push(q);
        focus = `q-${q.id}-text`;
      }
      break;
    case "q-up":
    case "q-down":
    case "q-del":
      if (v) {
        const qi = v.questions.findIndex((q) => q.id === d.qid);
        if (qi < 0) return;
        if (act === "q-del") v.questions.splice(qi, 1);
        else move(v.questions, qi, act === "q-up" ? -1 : 1);
      }
      break;
    case "beat-add":
      if (v) {
        const bt = newBeat({ audio: "[Musik]" });
        v.beats.push(bt);
        focus = `bt-${bt.id}-visual`;
      }
      break;
    case "beat-up":
    case "beat-down":
    case "beat-del":
    case "opt-add":
    case "opt-del":
    case "opt-use": {
      if (!v) return;
      const bi = v.beats.findIndex((x) => x.id === d.bid);
      const bt = v.beats[bi];
      if (!bt) return;
      if (act === "beat-up" || act === "beat-down") move(v.beats, bi, act === "beat-up" ? -1 : 1);
      if (act === "beat-del") {
        const fn = () => v.beats.splice(v.beats.indexOf(bt), 1);
        if (bt.visual.trim() || parseSound(bt.audio).text.replace(/\[[^\]]*\]/g, "").trim() || bt.options.length) return confirmDel("Diese Zeile löschen?", fn);
        fn();
      }
      if (act === "opt-add") {
        bt.options.push("");
        focus = `bt-o-${bt.id}-${bt.options.length - 1}`;
      }
      if (act === "opt-del") bt.options.splice(Number(d.k), 1);
      if (act === "opt-use") {
        // Alternative wird Hauptsatz, der bisherige Satz wandert in die Auswahl; «Name:» bleibt
        const oi = Number(d.k);
        const snd = parseSound(bt.audio);
        const pick = bt.options[oi] ?? "";
        if (snd.text.trim()) bt.options[oi] = snd.text;
        else bt.options.splice(oi, 1);
        bt.audio = snd.kind === "bild" ? pick : `${snd.who}: ${pick}`;
        syncBeat(bt);
      }
      break;
    }
    default:
      return;
  }
  scheduleSave(300);
  paint(focus);
}
/* ---------- PDF ---------- */

async function openPdf(kind: string, download: boolean, version?: string) {
  if (!ED) return;
  await saveNow();
  const url = `/api/scripts/${encodeURIComponent(ED.id)}/pdf?kind=${kind}${version ? `&version=${encodeURIComponent(version)}` : ""}${download ? "&download=1" : ""}`;
  if (download) {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    H.toast(kind === "shotlist" ? "Drehliste wird heruntergeladen" : "Drehbuch wird heruntergeladen");
  } else window.open(url, "_blank", "noopener");
}

/* ---------- Als Datei sichern und übernehmen ---------- */

const FILE_KIND = "socialsolve-drehbuch";
function exportScript() {
  const s = ED?.s;
  if (!s) return;
  const data = { kind: FILE_KIND, title: s.title, type: s.type, client: clientName(s.clientId), content: s.content };
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  a.download = `${s.title.replace(/[^\p{L}\p{N} _-]+/gu, "").replace(/\s+/g, "_") || "Skript"}.drehbuch.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
/** Skript aus einer Datei übernehmen; der Kunde wird über den Namen zugeordnet, falls es ihn gibt. */
function importScript() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".json,application/json";
  input.hidden = true;
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    input.remove();
    if (!f) return;
    void (async () => {
      try {
        if (f.size > 1_500_000) throw new Error("Die Datei ist zu gross.");
        const d = JSON.parse(await f.text()) as { kind?: string; title?: string; type?: string; client?: string; content?: unknown };
        if (d.kind !== FILE_KIND || !d.title || !d.content) throw new Error("Das ist keine Skript-Datei aus dem Management.");
        const type = (d.type && d.type in SCRIPT_TYPES ? d.type : "social") as ScriptType;
        const want = String(d.client ?? "").trim().toLowerCase();
        const client = want ? H.clients().find((c) => c.name.trim().toLowerCase() === want) ?? H.clients().find((c) => c.name.toLowerCase().includes(want.split(" ")[0] ?? want)) : undefined;
        const { script } = await api.createScript({ title: String(d.title).slice(0, 200), type, clientId: client?.id ?? null, content: d.content });
        LIST = undefined;
        H.toast(client ? `«${script.title}» übernommen und ${client.name} zugeordnet` : `«${script.title}» übernommen${d.client ? ` (Kunde «${d.client}» gibt es hier noch nicht)` : ""}`);
        H.go(`skript-${script.id}`);
      } catch (e) {
        if (!H.authLost(e)) H.toast(e instanceof SyntaxError ? "Die Datei lässt sich nicht lesen." : e instanceof Error ? e.message : failMsg(e));
      }
    })();
  });
  document.body.appendChild(input);
  input.click();
}

/* ---------- Teleprompter ---------- */

const TP = { speed: 40, size: 56, mirror: false, playing: false, raf: 0, last: 0, pos: 0 };
function prompter() {
  const s = ED?.s;
  if (!s) return;
  const c = s.content;
  // Im gewählten Video nur dessen Text, sonst alle Videos nacheinander
  const only = c.videos.find((v) => v.id === ED?.sel);
  const blocks = (only ? [only] : c.videos)
    .map((v) => {
      const head = c.videos.length > 1 && !only ? `<h2 class="tp-video">${esc(videoLabel(c, v))}</h2>` : "";
      const secs = v.beats
        .map((b) => parseSound(b.audio))
        .filter((x) => x.kind !== "bild" && x.text.trim())
        .map((x) => {
          const text = esc(x.text).replace(/\[([^\]]*)\]/g, '<span class="tp-dir">[$1]</span>').replace(/\n/g, "<br>");
          return `<section><h3>${esc(x.who)}</h3><p>${text}</p></section>`;
        })
        .join("");
      return secs ? head + secs : "";
    })
    .join("");
  const el = document.createElement("div");
  el.className = "tp";
  el.id = "tp";
  el.innerHTML = `<div class="tp-scroll" id="tp-scroll"><div class="tp-text ${TP.mirror ? "mirror" : ""}" id="tp-text" style="font-size:${TP.size}px">${blocks || `<section><p class="tp-dir">Noch kein Sprechtext erfasst.</p></section>`}<div class="tp-end">Ende</div></div></div>
    <div class="tp-guide"></div>
    <div class="tp-bar"><button data-s="tp-play" class="tp-btn pri" id="tp-play">${ic("play", 18)}<span>Start</span></button>
      <label>Tempo <input type="range" min="10" max="160" value="${TP.speed}" data-s="tp-speed"></label>
      <label>Schrift <input type="range" min="28" max="110" value="${TP.size}" data-s="tp-size"></label>
      <button data-s="tp-mirror" class="tp-btn ${TP.mirror ? "on" : ""}">Spiegeln</button>
      <button data-s="tp-close" class="tp-btn" aria-label="Schliessen">${ic("x", 18)}</button></div>
    <div class="tp-help">Leertaste: Start/Pause · ↑ ↓: Tempo · Esc: Schliessen</div>`;
  document.body.appendChild(el);
  TP.pos = 0;
  TP.playing = false;
  el.requestFullscreen?.().catch(() => undefined);
}
function tpTick(t: number) {
  const sc = document.getElementById("tp-scroll");
  if (!sc || !TP.playing) return;
  const dt = TP.last ? (t - TP.last) / 1000 : 0;
  TP.last = t;
  TP.pos += TP.speed * dt * (TP.size / 56);
  sc.scrollTop = TP.pos;
  if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 2) return tpToggle(false);
  TP.raf = requestAnimationFrame(tpTick);
}
function tpToggle(on = !TP.playing) {
  TP.playing = on;
  cancelAnimationFrame(TP.raf);
  const sc = document.getElementById("tp-scroll");
  if (sc) TP.pos = sc.scrollTop;
  TP.last = 0;
  const b = document.getElementById("tp-play");
  if (b) b.innerHTML = `${ic(on ? "pause" : "play", 18)}<span>${on ? "Pause" : "Start"}</span>`;
  if (on) TP.raf = requestAnimationFrame(tpTick);
}
function tpClose() {
  tpToggle(false);
  document.getElementById("tp")?.remove();
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
}

/** Aktionen direkt aus der Übersicht, ohne das Skript zu öffnen. */
async function listAction(act: string, id: string, k: string) {
  const item = LIST?.find((x) => x.id === id);
  if (!item) return;
  try {
    if (act === "c-status") {
      await api.saveScript(id, { status: k as ScriptStatus });
      item.status = k as ScriptStatus;
      H.toast(`«${item.title}» → ${SCRIPT_STATUS[k as ScriptStatus]}`);
    }
    if (act === "c-dup") {
      await api.duplicateScript(id);
      H.toast("Kopie erstellt");
      await loadList();
      return;
    }
    if (act === "c-del") {
      return H.confirm(`«${esc(item.title)}» mit allen Versionen endgültig löschen?`, "Löschen", async () => {
        H.closeModal();
        try {
          await api.deleteScript(id);
          LIST = LIST?.filter((x) => x.id !== id) ?? LIST;
          H.toast("Skript gelöscht");
          H.rerender();
        } catch (e) {
          if (!H.authLost(e)) H.toast(failMsg(e));
        }
      });
    }
    H.rerender();
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  }
}

/* ============ Ereignisse ============ */

document.addEventListener("click", (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>("[data-s]");
  if (!el || el.tagName === "SELECT" || el.tagName === "INPUT" || el.tagName === "TEXTAREA") return;
  const act = el.dataset.s ?? "";
  e.preventDefault();
  el.closest("details.menu")?.removeAttribute("open");
  const s = ED?.s;
  switch (act) {
    case "new":
      return newModal();
    case "new-type":
      return newModal(el.dataset.k as ScriptType);
    case "new-pick":
      NEW.type = el.dataset.k as ScriptType;
      document.querySelectorAll(".sk-typecard[data-s=new-pick]").forEach((x) => x.classList.toggle("on", x === el));
      return;
    case "lf-status":
      LF.status = (el.dataset.k ?? "") as "" | ScriptStatus;
      return H.rerender();
    case "show":
      if (!ED) return;
      ED.open.add(el.dataset.id ?? "");
      paint(el.dataset.focus);
      return;
    case "pv-jump":
      if (!ED || !el.dataset.k) return;
      ED.sel = el.dataset.k;
      paint();
      return;
    case "c-status":
    case "c-dup":
    case "c-del":
      return void listAction(act, el.dataset.id ?? "", el.dataset.k ?? "");
    case "pdf":
      return void openPdf(el.dataset.k ?? "script", true);
    case "pdf-view":
      return void openPdf(el.dataset.k ?? "script", false);
    case "versions":
      return versionsModal();
    case "v-pdf":
      return void openPdf("script", true, el.dataset.id);
    case "v-restore": {
      const v = ED?.versions?.find((x) => x.id === el.dataset.id);
      if (!ED || !v) return;
      const ed = ED;
      return H.confirm(`Den Inhalt von v${v.version} in die aktuelle Fassung holen? Der jetzige Stand wird überschrieben (schliesse ihn vorher als Version ab, wenn du ihn behalten willst).`, "Zurückholen", async () => {
        H.closeModal();
        await saveNow();
        try {
          const { script } = await api.restoreScript(ed.id, v.id);
          ed.s = script;
          ed.base = script.updatedAt;
          paint();
          H.toast(`Inhalt von v${v.version} geladen`);
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    }
    case "freeze":
      if (!s) return;
      H.openModal(
        `${H.mhead(`Version ${s.version} abschliessen`)}<p class="muted" style="margin:0">v${s.version} wird eingefroren und bleibt als PDF abrufbar. Du arbeitest danach an v${s.version + 1} weiter.</p><form data-sform="freeze" class="stack" style="gap:14px"><div class="field"><label for="sk-v-note">Notiz</label><input class="in" id="sk-v-note" placeholder="z. B. An Kunden geschickt, Feedback Runde 1" maxlength="500" autocomplete="off"></div><div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Abschliessen</button></div></form>`,
      );
      return;
    case "dup":
      if (!ED) return;
      return void (async () => {
        await saveNow();
        try {
          const { script } = await api.duplicateScript(ED!.id);
          LIST = undefined;
          H.toast("Kopie erstellt");
          H.go(`skript-${script.id}`);
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      })();
    case "del":
      if (!s || !ED) return;
      return H.confirm(`«${esc(s.title)}» mit allen Versionen endgültig löschen?`, "Löschen", async () => {
        H.closeModal();
        const id = ED!.id;
        clearTimeout(ED!.timer);
        ED = null;
        try {
          await api.deleteScript(id);
          LIST = undefined;
          H.toast("Skript gelöscht");
          H.go("skripte");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "reload":
      if (!ED) return;
      ED.conflict = false;
      ED.dirty = false;
      ED.s = null;
      paint();
      return void loadScript(ED.id);
    case "prompter":
      return prompter();
    case "export":
      return exportScript();
    case "import":
      return importScript();
    case "tp-play":
      return tpToggle();
    case "tp-mirror":
      TP.mirror = !TP.mirror;
      el.classList.toggle("on", TP.mirror);
      document.getElementById("tp-text")?.classList.toggle("mirror", TP.mirror);
      return;
    case "tp-close":
      return tpClose();
    default:
      structural(act, el);
  }
});

document.addEventListener("input", (e) => {
  const el = e.target as HTMLInputElement | HTMLTextAreaElement;
  const act = el.dataset?.s;
  if (act === "tp-speed") TP.speed = Number(el.value);
  if (act === "tp-size") {
    TP.size = Number(el.value);
    const t = document.getElementById("tp-text");
    if (t) t.style.fontSize = `${TP.size}px`;
  }
  if (!ED?.s) return;
  if (act === "f") {
    setField(el.dataset.p ?? "", el.value);
    if (el instanceof HTMLTextAreaElement) grow(el);
    afterInput(el.dataset.p ?? "");
    scheduleSave();
  } else if (act === "meta" && el.dataset.p === "title") {
    ED.s.title = el.value.trim() ? el.value : ED.s.title;
    refreshPreview();
    scheduleSave();
  }
});

document.addEventListener("change", (e) => {
  const el = e.target as HTMLSelectElement;
  const act = el.dataset?.s;
  if (act === "lf-client") {
    LF.client = el.value;
    return H.rerender();
  }
  if (act === "new-client") {
    const p = document.getElementById("sk-n-project");
    if (p) p.innerHTML = projectOptions(el.value, "");
    return;
  }
  if (!ED?.s) return;
  const s = ED.s;
  if (act === "status") {
    s.status = el.value as ScriptStatus;
    el.closest(".pill-sel")!.className = `pill-sel tone-${s.status === "approved" ? "done" : s.status === "review" ? "review" : "open"}`;
    scheduleSave(100);
    if (s.status === "review" && !ED.versions?.some((v) => v.version === s.version)) H.toast(`Tipp: Schliesse v${s.version} über «…» ab, sobald du sie dem Kunden schickst.`);
  } else if (act === "meta" && el.dataset.p === "clientId") {
    s.clientId = el.value || null;
    const pr = s.projectId ? H.projects().find((p) => p.id === s.projectId) : null;
    if (pr && s.clientId && pr.client !== s.clientId) s.projectId = null;
    scheduleSave(100);
    paint();
  } else if (act === "meta" && el.dataset.p === "projectId") {
    s.projectId = el.value || null;
    const pr = H.projects().find((p) => p.id === el.value);
    if (pr && !s.clientId) s.clientId = pr.client;
    scheduleSave(100);
    paint();
  } else if (act === "f" && (el as unknown as HTMLInputElement).type === "date") {
    setField(el.dataset.p ?? "", el.value);
    afterInput(el.dataset.p ?? "");
    scheduleSave(300);
  } else if (act === "f" && el.tagName === "SELECT") {
    // Auswahlfelder lösen kein «input» in jedem Browser aus
    setField(el.dataset.p ?? "", el.value);
    afterInput(el.dataset.p ?? "");
    scheduleSave(300);

  }
});

document.addEventListener("submit", (e) => {
  const f = (e.target as HTMLElement).closest<HTMLFormElement>("[data-sform]");
  if (!f) return;
  e.preventDefault();
  const btn = f.querySelector<HTMLButtonElement>("button[type=submit]");
  if (f.dataset.sform === "new") void createFromModal(btn);
  if (f.dataset.sform === "freeze" && ED?.s) {
    const ed = ED;
    const note = (document.getElementById("sk-v-note") as HTMLInputElement | null)?.value.trim() ?? "";
    if (btn) btn.disabled = true;
    void (async () => {
      await saveNow();
      try {
        const { script } = await api.freezeScript(ed.id, note);
        H.closeModal();
        ed.s = script;
        ed.base = script.updatedAt;
        ed.versions = undefined;
        paint();
        H.toast(`v${script.version - 1} abgeschlossen. Du arbeitest jetzt an v${script.version}.`);
      } catch (err) {
        if (!H.authLost(err)) H.toast(failMsg(err));
        if (btn) btn.disabled = false;
      }
    })();
  }
});

document.addEventListener(
  "keydown",
  (e) => {
    if (document.getElementById("tp")) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        return tpClose();
      }
      if (e.key === " ") {
        e.preventDefault();
        return tpToggle();
      }
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        TP.speed = Math.max(10, Math.min(160, TP.speed + (e.key === "ArrowUp" ? 8 : -8)));
        const r = document.querySelector<HTMLInputElement>("[data-s=tp-speed]");
        if (r) r.value = String(TP.speed);
      }
      return;
    }
    if (!ED?.s || !document.activeElement?.closest(".sk-ed")) return;
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      if (ED.dirty) void saveNow();
      else H.toast("Alles gespeichert");
    }
    // Cmd/Ctrl + Enter in einer Einstellung: neue Einstellung darunter
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      const el = document.activeElement.closest<HTMLElement>("[data-beat]");
      if (!el) return;
      e.preventDefault();
      const v = videoById(el.dataset.vid ?? "");
      const bi = v?.beats.findIndex((x) => x.id === el.dataset.beat) ?? -1;
      const cur = v?.beats[bi];
      if (!v || !cur) return;
      const bt = newBeat({ shot: cur.shot });
      v.beats.splice(bi + 1, 0, bt);
      scheduleSave(300);
      paint(`bt-${bt.id}-visual`);
    }
  },
  true,
);

/* ============ Auf der Kundenseite ============ */

const CLIENT_LISTS: Record<string, ScriptListItem[] | null> = {};
export function clientScripts(cid: string): string {
  if (!(cid in CLIENT_LISTS)) {
    CLIENT_LISTS[cid] = null;
    void api
      .scripts(cid)
      .then((r) => {
        CLIENT_LISTS[cid] = r.scripts;
        const box = document.getElementById(`sk-client-${cid}`);
        if (box) box.innerHTML = clientScriptsInner(cid);
      })
      .catch(() => {
        CLIENT_LISTS[cid] = [];
      });
  }
  return `<section class="panel"><div class="panel-head"><h2 class="h2">Skripte</h2><button class="btn sm" data-s="new-for" data-id="${esc(cid)}">${ic("plus", 15)}Neues Skript</button></div><div id="sk-client-${esc(cid)}">${clientScriptsInner(cid)}</div></section>`;
}
function clientScriptsInner(cid: string) {
  const l = CLIENT_LISTS[cid];
  if (!l) return `<span class="dim small">Lädt …</span>`;
  if (!l.length) return `<span class="muted small">Noch keine Skripte für diesen Kunden.</span>`;
  return l
    .slice(0, 6)
    .map((s) => `<a class="row sk-crow" href="#skript-${esc(s.id)}"><span class="lic">${ic(TYPE_ICON[s.type], 16)}</span><span class="grow"><b class="ellip" style="display:block;font-weight:500">${esc(s.title)}</b><span class="dim small">v${s.version} · ${s.seconds ? fmtSeconds(s.seconds) : "noch leer"} · ${ago(s.updatedAt)}</span></span><span class="chip ${STATUS_CHIP[s.status]}">${SCRIPT_STATUS[s.status]}</span></a>`)
    .join("");
}
document.addEventListener(
  "click",
  (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-s=new-for]");
    if (!el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    delete CLIENT_LISTS[el.dataset.id ?? ""];
    newModal(undefined, el.dataset.id);
  },
  true,
);
/** Nach dem Wechsel auf eine andere Seite die Kundenlisten neu laden lassen. */
export const forgetClientScripts = () => {
  for (const k of Object.keys(CLIENT_LISTS)) delete CLIENT_LISTS[k];
};
